import React, { useEffect, useMemo, useState } from 'react';
import apiClient from '../api/client';
import './Dashboard.css';
import { useDistanceUnit } from '../contexts/DistanceUnitContext';
import { useStravaModal } from '../contexts/StravaModalContext';
import { useNotifications } from '../contexts/NotificationContext';
import { useNavigate } from 'react-router-dom';
import RouteMap from './RouteMap';
import Sidebar from './Sidebar';
import EmailVerificationBanner from './EmailVerificationBanner';
import FirstTimeUserBanner from './FirstTimeUserBanner';
import { parseActivityDate } from '../utils/dateUtils';

// Import assets
import iconMenu from '../assets/icon-menu.svg';
import iconSearch from '../assets/icon-search.svg';
import iconChartUp from '../assets/icon-chart-up.svg';
import iconGauge from '../assets/icon-gauge.svg';
import iconCalendar from '../assets/icon-calendar.svg';
import iconStreak from '../assets/icon-streak.svg';
import NotificationBell from './NotificationBell';
import ProfilePicture from './ProfilePicture';

const Dashboard = ({ onLogout }) => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [activities, setActivities] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [profileData, setProfileData] = useState(null);
  const [commonRoute, setCommonRoute] = useState(null);
  const [fastestRuns, setFastestRuns] = useState([]);
  const [showVerificationBanner, setShowVerificationBanner] = useState(true);
  const [showFirstTimeUserBanner, setShowFirstTimeUserBanner] = useState(false);
  const [user, setUser] = useState(() => JSON.parse(localStorage.getItem('user') || '{}'));
  const { convertDistance, getUnitLabel, unit } = useDistanceUnit();
  const { openModal: openStravaModal, registerStatusListener } = useStravaModal();
  const { refreshNotifications } = useNotifications();
  const navigate = useNavigate();

  // Get current user to check email verification status
  const needsVerification = user && !user.email_verified;

  // Listen for user data changes in localStorage
  useEffect(() => {
    const handleStorageChange = () => {
      const updatedUser = JSON.parse(localStorage.getItem('user') || '{}');
      setUser(updatedUser);
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handleFastestRunClick = (activityId) => {
    navigate('/activities', { state: { selectedActivityId: activityId } });
  };

  useEffect(() => {
    const pendingPrompt = localStorage.getItem("stravaSyncPrompt");
    if (pendingPrompt) {
      localStorage.removeItem("stravaSyncPrompt");
      openStravaModal({ tab: pendingPrompt });
    }
    const unsubscribe = registerStatusListener(() => {
      // Status listener registered for Strava modal functionality
    });
    return unsubscribe;
  }, [openStravaModal, registerStatusListener]);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        // Fetch activities (JWT token is automatically added by apiClient)
        const [activitiesResponse, statsResponse, profileResponse, commonRouteResponse, fastestRunsResponse] = await Promise.all([
          apiClient.get('/activities'),
          apiClient.get('/activities/runstreak'),
          apiClient.get('/profile').catch((error) => {
            console.error('Error fetching profile:', error);
            return { data: { profile: null } };
          }),
          apiClient.get('/activities/most-common-route').catch((error) => {
            console.error('Error fetching common route:', error);
            return { data: { route: null, count: 0 } };
          }),
          apiClient.get('/activities/most-common-route/fastest-runs').catch((error) => {
            console.error('Error fetching fastest runs:', error);
            return { data: { fastest_runs: [] } };
          })
        ]);

        setActivities(activitiesResponse.data.activities);
        setStats(statsResponse.data);
        setProfileData(profileResponse.data.profile);
        setCommonRoute(commonRouteResponse.data);
        setFastestRuns(fastestRunsResponse.data.fastest_runs);

        // Check if we should show the first-time user banner
        const hasActivities = activitiesResponse.data.activities.length > 0;
        const hasDismissedBanner = localStorage.getItem('firstTimeUserBannerDismissed');

        // Show banner if user has no activities AND hasn't dismissed it before
        if (!hasActivities && !hasDismissedBanner) {
          setShowFirstTimeUserBanner(true);
        }

        refreshNotifications();
        setLoading(false);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [refreshNotifications]);
  const weekDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  const {
    weeklyData,
    weeklyProgressLabel,
    weeklyProgressValue,
    allWeeklyZero,
    bestPace,
    weeklyMileageTrend,
    weeklyMileageLabels,
    weeklyMileageMax,
    allWeeklyMileageZero,
    yAxisMax
  } = useMemo(() => {
    const distanceInPreferredUnit = (meters = 0) => {
      const value = Number.parseFloat(convertDistance(meters));
      return Number.isFinite(value) ? value : 0;
    };

    const calculatePaceMinutes = (distanceMeters = 0, movingTimeSeconds = 0) => {
      if (!distanceMeters || !movingTimeSeconds) return null;
      const distanceInUnit = unit === 'miles'
        ? distanceMeters / 1609.34
        : distanceMeters / 1000;
      if (!distanceInUnit) return null;
      return (movingTimeSeconds / 60) / distanceInUnit;
    };

    const formatPace = (paceMinutes) => {
      if (!paceMinutes) return '--';
      const minutes = Math.floor(paceMinutes);
      const seconds = Math.round((paceMinutes - minutes) * 60)
        .toString()
        .padStart(2, '0');
      return `${minutes}:${seconds}/${getUnitLabel()}`;
    };

    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const startOfWeek = new Date(now);
    const dayOfWeek = startOfWeek.getDay(); // Sunday = 0
    const diffToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    startOfWeek.setDate(startOfWeek.getDate() + diffToMonday);

    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(endOfWeek.getDate() + 7);

    const previousWeekStart = new Date(startOfWeek);
    previousWeekStart.setDate(previousWeekStart.getDate() - 7);
    const previousWeekEnd = new Date(startOfWeek);

    const weeklyTotals = new Array(7).fill(0);
    const previousWeeklyTotals = new Array(7).fill(0);

    // Calculate last 5 weeks of mileage for trend chart
    const weeklyMileageTrend = new Array(5).fill(0);
    const weeklyMileageLabels = [];

    // Create array of week start dates for last 5 weeks
    const weekStarts = [];
    for (let i = 4; i >= 0; i--) {
      const weekStart = new Date(startOfWeek);
      weekStart.setDate(weekStart.getDate() - (i * 7));
      weekStarts.push(weekStart);

      // Create label (e.g., "Nov 4")
      const label = weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      weeklyMileageLabels.push(label);
    }

    const paceEntries = [];

    activities.forEach((activity) => {
      const activityDate = parseActivityDate(activity);
      if (!activityDate) return;

      const dayIndex = (activityDate.getDay() + 6) % 7; // Normalize Monday -> 0
      const distanceValue = distanceInPreferredUnit(activity.distance);

      // Add to current week's daily totals
      if (activityDate >= startOfWeek && activityDate < endOfWeek) {
        weeklyTotals[dayIndex] += distanceValue;
      } else if (activityDate >= previousWeekStart && activityDate < previousWeekEnd) {
        previousWeeklyTotals[dayIndex] += distanceValue;
      }

      // Add to weekly mileage trend (last 5 weeks)
      for (let i = 0; i < 5; i++) {
        const weekStart = weekStarts[i];
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekEnd.getDate() + 7);

        if (activityDate >= weekStart && activityDate < weekEnd) {
          weeklyMileageTrend[i] += distanceValue;
          break;
        }
      }

      const paceMinutes = calculatePaceMinutes(activity.distance, activity.moving_time);
      if (paceMinutes) {
        paceEntries.push({
          date: activityDate,
          paceMinutes
        });
      }
    });

    const totalThisWeek = weeklyTotals.reduce((sum, value) => sum + value, 0);
    const totalPreviousWeek = previousWeeklyTotals.reduce((sum, value) => sum + value, 0);

    let weeklyProgressValue = 0;
    if (totalPreviousWeek === 0) {
      weeklyProgressValue = totalThisWeek > 0 ? 100 : 0;
    } else {
      weeklyProgressValue = ((totalThisWeek - totalPreviousWeek) / totalPreviousWeek) * 100;
    }

    const weeklyProgressLabel = weeklyProgressValue === 0
      ? 'No change'
      : `${Math.abs(Math.round(weeklyProgressValue))}% ${weeklyProgressValue > 0 ? 'more' : 'less'}`;

    paceEntries.sort((a, b) => a.date - b.date);
    const paceSeriesData = paceEntries.slice(-7);
    const paceValues = paceSeriesData.map((entry) => entry.paceMinutes);

    const bestPaceMinutes = paceValues.length ? Math.min(...paceValues) : null;
    const bestPaceLabel = formatPace(bestPaceMinutes);

    // Calculate max for weekly mileage trend
    const weeklyMileageMax = Math.max(...weeklyMileageTrend);
    const allWeeklyMileageZero = weeklyMileageTrend.every(value => value === 0);

    const maxWeeklyValue = Math.max(...weeklyTotals);
    // Calculate nice Y-axis max (round up to nearest nice number)
    const calculateYAxisMax = (maxValue) => {
      if (maxValue === 0) return 12; // Default when no data
      const magnitude = Math.pow(10, Math.floor(Math.log10(maxValue)));
      const normalized = maxValue / magnitude;
      let niceMax;
      if (normalized <= 1) niceMax = 1;
      else if (normalized <= 2) niceMax = 2;
      else if (normalized <= 5) niceMax = 5;
      else niceMax = 10;
      return niceMax * magnitude;
    };

    const yAxisMax = calculateYAxisMax(maxWeeklyValue);

    return {
      weeklyData: weeklyTotals,
      weeklyProgressLabel,
      bestPace: bestPaceLabel,
      weeklyMileageTrend,
      weeklyMileageLabels,
      weeklyMileageMax,
      allWeeklyMileageZero,
      yAxisMax,
      weeklyProgressValue,
      allWeeklyZero: weeklyTotals.every((value) => value === 0)
    };
  }, [activities, convertDistance, getUnitLabel, unit]);

  if (loading) {
    return (
      <div className="dashboard-new">
        <div className="dashboard-loading">Loading dashboard...</div>
      </div>
    );
  }

  // Calculate stats
  const currentStreak = stats?.current_streak || 0;
  const longestStreak = stats?.longest_streak || 0;
  const totalRuns = activities.length || 0;
  const totalDistance = activities.reduce((sum, a) => sum + (a.distance || 0), 0);
  const averageDistance = totalRuns > 0 ? totalDistance / totalRuns : 0;

  return (
    <div className="dashboard-new">
      {/* Sidebar */}
      <Sidebar isOpen={sidebarOpen} />

      {/* Main Container */}
      <div className={`dashboard-main-container ${sidebarOpen ? 'sidebar-open' : 'sidebar-closed'}`}>
      {/* Header */}
      <header className="dashboard-header">
        <div className="header-left">
          <button className="icon-button" onClick={toggleSidebar}>
            <img src={iconMenu} alt="Menu" />
          </button>
          <h1 className="header-title">Runstreak Dashboard</h1>
        </div>
        <div className="header-right">
          <div className="search-container">
            <img src={iconSearch} alt="Search" className="search-icon" />
            <input
              type="text"
              placeholder="Search activities..."
              className="search-input"
            />
          </div>
          <NotificationBell />
          <button className="logout-button" onClick={onLogout}>Logout</button>
          <ProfilePicture
            profilePictureUrl={profileData?.profile_picture_url}
            firstName={profileData?.first_name}
            lastName={profileData?.last_name}
            email={user?.email}
            size="small"
            showInitials={true}
          />
        </div>
      </header>

      {/* Email Verification Banner */}
      {needsVerification && showVerificationBanner && (
        <EmailVerificationBanner
          user={user}
          onDismiss={() => setShowVerificationBanner(false)}
        />
      )}

      {/* First Time User Banner */}
      {showFirstTimeUserBanner && (
        <FirstTimeUserBanner
          onDismiss={() => {
            setShowFirstTimeUserBanner(false);
            localStorage.setItem('firstTimeUserBannerDismissed', 'true');
          }}
        />
      )}

      {/* Main Content */}
      <main className="dashboard-content">
        {/* Current Streak Hero */}
        <section className="hero-streak">
          <div className="hero-left">
            <div className="streak-badge">
              <img src={iconStreak} alt="Streak" />
              <span>Current Streak</span>
            </div>
            <h2 className="dashboard-streak-number">{currentStreak}</h2>
            <p className="streak-text">Days running strong!</p>
          </div>
          <div className="hero-right">
            <p className="milestone-label">Next milestone</p>
            <p className="milestone-value">50 days</p>
            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{ width: `${(currentStreak / 50) * 100}%` }}
              ></div>
            </div>
          </div>
        </section>

        {/* Stats Cards Row */}
        <div className="stats-row">
          <div className="stat-card">
            <div className="stat-card-header">
              <img src={iconChartUp} alt="Weekly Progress" />
              <span>Weekly Progress</span>
            </div>
            <div className="stat-card-content">
              <p className="stat-value">{weeklyProgressLabel}</p>
              <p className="stat-description">
                {weeklyProgressValue === 0
                  ? 'Same distance as last week'
                  : `${weeklyProgressValue > 0 ? 'more' : 'less'} distance than last week`}
              </p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-card-header">
              <img src={iconGauge} alt="Best Pace" />
              <span>Best Pace</span>
            </div>
            <div className="stat-card-content">
              <p className="stat-value">{bestPace}</p>
              <p className="stat-description">Personal best this month</p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-card-header">
              <img src={iconCalendar} alt="Longest Streak" />
              <span>Longest Streak</span>
            </div>
            <div className="stat-card-content">
              <p className="stat-value">{longestStreak} days</p>
              <p className="stat-description">Your personal record</p>
            </div>
          </div>
        </div>

        {/* Charts Row */}
        <div className="charts-row">
          <div className="chart-card">
            <div className="chart-header">
              <h3>Weekly Activity</h3>
              <p>Distance covered each day this week</p>
            </div>
            <div className="chart-content">
              <div className="bar-chart-wrapper">
                <div className="chart-y-axis">
                  <span className="y-axis-label">{Math.round(yAxisMax)}</span>
                  <span className="y-axis-label">{Math.round(yAxisMax * 0.75)}</span>
                  <span className="y-axis-label">{Math.round(yAxisMax * 0.5)}</span>
                  <span className="y-axis-label">{Math.round(yAxisMax * 0.25)}</span>
                  <span className="y-axis-label">0</span>
                </div>
                <div className="bar-chart-area">
                  <div className="chart-grid-lines">
                    <div className="grid-line"></div>
                    <div className="grid-line"></div>
                    <div className="grid-line"></div>
                    <div className="grid-line"></div>
                    <div className="grid-line"></div>
                  </div>
                  <div className="bar-chart">
                    {weekDays.map((day, index) => {
                      const distanceValue = weeklyData[index] || 0;
                      const isZero = distanceValue <= 0;
                      const normalizedHeight = yAxisMax === 0 ? 0 : (distanceValue / yAxisMax) * 100;
                      const adjustedHeight = allWeeklyZero
                        ? 0
                        : isZero
                          ? 3
                          : Math.max(Math.min(normalizedHeight, 100), 8);
                      const displayValue = distanceValue > 0 ? distanceValue.toFixed(1) : null;
                      return (
                        <div key={day} className="bar-column">
                          <div
                            className={`bar ${isZero ? 'bar-empty' : ''}`}
                            style={{ height: `${adjustedHeight}%` }}
                          >
                            {displayValue && <span className="bar-value">{displayValue}</span>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="bar-chart-labels">
                    {weekDays.map((day) => (
                      <span key={day} className="bar-label">{day}</span>
                    ))}
                  </div>
                </div>
              </div>
              {allWeeklyZero && (
                <div className="chart-empty">No runs logged yet this week. Lace up and log a run!</div>
              )}
            </div>
          </div>

          <div className="chart-card">
            <div className="chart-header">
              <h3>Weekly Mileage Trend</h3>
              <p>Total distance per week (last 5 weeks)</p>
            </div>
            <div className="chart-content">
              <div className="bar-chart-wrapper">
                <div className="chart-y-axis">
                  <span className="y-axis-label">{Math.round(weeklyMileageMax)}</span>
                  <span className="y-axis-label">{Math.round(weeklyMileageMax * 0.75)}</span>
                  <span className="y-axis-label">{Math.round(weeklyMileageMax * 0.5)}</span>
                  <span className="y-axis-label">{Math.round(weeklyMileageMax * 0.25)}</span>
                  <span className="y-axis-label">0</span>
                </div>
                <div className="bar-chart-area">
                  <div className="chart-grid-lines">
                    <div className="grid-line"></div>
                    <div className="grid-line"></div>
                    <div className="grid-line"></div>
                    <div className="grid-line"></div>
                    <div className="grid-line"></div>
                  </div>
                  <div className="bar-chart">
                    {weeklyMileageTrend.map((distance, index) => {
                      const isZero = distance <= 0;
                      const normalizedHeight = weeklyMileageMax === 0 ? 0 : (distance / weeklyMileageMax) * 100;
                      const adjustedHeight = allWeeklyMileageZero
                        ? 0
                        : isZero
                          ? 3
                          : Math.max(Math.min(normalizedHeight, 100), 8);
                      const displayValue = distance > 0 ? distance.toFixed(1) : null;
                      return (
                        <div key={index} className="bar-column">
                          <div
                            className={`bar ${isZero ? 'bar-empty' : ''}`}
                            style={{ height: `${adjustedHeight}%` }}
                          >
                            {displayValue && <span className="bar-value">{displayValue}</span>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="bar-chart-labels">
                    {weeklyMileageLabels.map((label) => (
                      <span key={label} className="bar-label">{label}</span>
                    ))}
                  </div>
                </div>
              </div>
              {allWeeklyMileageZero && (
                <div className="chart-empty">No runs logged in the last 5 weeks. Time to get started!</div>
              )}
            </div>
          </div>
        </div>

        {/* Bottom Row */}
        <div className="bottom-row">
          {/* Most Common Route Map Card */}
          <div className="sync-card">
            <div className="sync-content">
              <div className="sync-text">
                <h3>Your Most Common Route</h3>
                {commonRoute && commonRoute.route ? (
                  <>
                    <p>You've run this route {commonRoute.count} time{commonRoute.count !== 1 ? 's' : ''}</p>
                    <span className="sync-subtext">
                      {commonRoute.route.name} - {parseFloat(convertDistance(commonRoute.route.distance)).toFixed(1)} {getUnitLabel()}
                    </span>
                    <div className="route-content">
                      <div className="route-map-container">
                        {commonRoute.route.summary_polyline && commonRoute.route.summary_polyline.trim() !== '' ? (
                          <RouteMap summaryPolyline={commonRoute.route.summary_polyline} height="200px" showPlaceholder={true} />
                        ) : (
                          <div style={{
                            height: '200px',
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: '#f3f4f6',
                            borderRadius: '8px',
                            color: '#6b7280',
                            fontSize: '14px',
                            border: '1px solid #d1d5db'
                          }}>
                            No route map data available for this activity
                          </div>
                        )}
                      </div>
                      {fastestRuns.length > 0 && (
                        <div className="fastest-runs-list">
                          <h4>Top 5 Fastest Runs</h4>
                          <div className="fastest-runs-items">
                            {fastestRuns.map((run, index) => {
                              const paceMinutes = unit === 'miles'
                                ? run.pace * 1.60934
                                : run.pace;
                              const minutes = Math.floor(paceMinutes);
                              const seconds = Math.round((paceMinutes - minutes) * 60).toString().padStart(2, '0');
                              const runDate = new Date(run.start_date);
                              const formattedDate = runDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

                              return (
                                <div
                                  key={run.id}
                                  className="fastest-run-item"
                                  onClick={() => handleFastestRunClick(run.id)}
                                  role="button"
                                  tabIndex={0}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      handleFastestRunClick(run.id);
                                    }
                                  }}
                                >
                                  <div className="fastest-run-rank">#{index + 1}</div>
                                  <div className="fastest-run-details">
                                    <div className="fastest-run-pace">{minutes}:{seconds}/{getUnitLabel()}</div>
                                    <div className="fastest-run-date">{formattedDate}</div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <p>No routes found yet. Keep logging your runs!</p>
                )}
              </div>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="quick-stats">
            <div className="quick-stat-card">
              <p className="quick-stat-label">Total Runs</p>
              <p className="quick-stat-value">{totalRuns}</p>
              <span className="quick-stat-badge">+3 this week</span>
            </div>

            <div className="quick-stat-card">
              <p className="quick-stat-label">Total Distance</p>
              <p className="quick-stat-value">{parseFloat(convertDistance(totalDistance)).toFixed(0)} {getUnitLabel()}</p>
              <span className="quick-stat-badge">+35 {getUnitLabel()}</span>
            </div>

            <div className="quick-stat-card">
              <p className="quick-stat-label">Avg Distance per Run</p>
              <p className="quick-stat-value">{parseFloat(convertDistance(averageDistance)).toFixed(1)} {getUnitLabel()}</p>
              <span className="quick-stat-badge">{totalRuns} runs</span>
            </div>
          </div>
        </div>
      </main>
      </div>
    </div>
  );
};

export default Dashboard;
